import { useEffect, useState } from "react";
import { api, inr, resolveImg, formatApiErrorDetail } from "../lib/api";
import { CheckCircle2, XCircle, Clock, Upload, Loader2, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  pending: { label: "Pending", cls: "bg-ochre/15 text-ochre", icon: Clock },
  verified: { label: "Verified", cls: "bg-sage/15 text-sage", icon: CheckCircle2 },
  rejected: { label: "Rejected", cls: "bg-destructive/10 text-destructive", icon: XCircle },
};

export default function AdminPaymentsPage() {
  const [rows, setRows] = useState(null);
  const [qr, setQr] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [uploadingQr, setUploadingQr] = useState(false);

  const loadRows = () => api.get("/admin/subscription-payments").then((r) => setRows(r.data.requests || []));
  const loadQr = () => api.get("/admin/upi-config").then((r) => setQr(r.data));

  useEffect(() => { loadRows(); loadQr(); }, []);

  const act = async (id, action) => {
    setBusyId(id + action);
    try {
      await api.post(`/admin/subscription-payments/${id}/${action}`);
      toast.success(action === "verify" ? "Verified — subscription extended" : "Request rejected");
      await loadRows();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const uploadQr = async (file) => {
    if (!file) return;
    setUploadingQr(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await api.patch("/admin/upi-config", { qr_url: data.url });
      toast.success("QR image updated");
      await loadQr();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setUploadingQr(false);
    }
  };

  if (!rows) return <div className="text-charcoal-muted">Loading…</div>;

  return (
    <div data-testid="admin-payments-page">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-heading font-bold text-2xl text-charcoal">Subscription Payments</h2>
          <p className="text-sm text-charcoal-muted mt-1">Verify manual UPI payments from sellers to activate their subscription.</p>
        </div>
      </div>

      {/* UPI QR management */}
      <div className="bg-white border border-border p-5 mb-6" data-testid="admin-qr-manager">
        <h3 className="font-heading font-semibold text-charcoal mb-1">Payment QR &amp; UPI details</h3>
        <p className="text-xs text-charcoal-muted mb-4">This QR and UPI ID are shown on every seller's subscription page.</p>
        <div className="flex items-center gap-6 flex-wrap">
          {qr?.qr_url && (
            <img src={resolveImg(qr.qr_url)} alt="UPI QR" className="w-32 h-32 object-contain border border-border bg-off-white-alt p-1" data-testid="admin-current-qr" />
          )}
          <div className="text-sm space-y-1">
            <div><span className="text-charcoal-muted">UPI ID:</span> <span className="font-medium text-charcoal">{qr?.upi_id}</span></div>
            <div><span className="text-charcoal-muted">Holder:</span> <span className="font-medium text-charcoal">{qr?.holder_name}</span></div>
            <label className="inline-flex items-center gap-2 text-sm border border-border hover:border-terracotta px-3 py-2 cursor-pointer transition-colors mt-2" data-testid="qr-upload-label">
              {uploadingQr ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploadingQr ? "Uploading…" : "Upload new QR image"}
              <input type="file" accept="image/*" className="hidden" data-testid="qr-upload-input"
                onChange={(e) => uploadQr(e.target.files?.[0])} />
            </label>
          </div>
        </div>
      </div>

      {/* Requests table */}
      <div className="bg-white border border-border" data-testid="payments-table">
        <div className="px-5 py-4 border-b border-border">
          <h3 className="font-heading font-semibold text-charcoal">Payment submissions ({rows.length})</h3>
        </div>
        {rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-charcoal-muted">No payment submissions yet.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-off-white-alt text-xs uppercase tracking-[0.15em] text-charcoal-muted">
                <tr>
                  <th className="text-left px-4 py-3">Seller</th>
                  <th className="text-right px-4 py-3">Amount</th>
                  <th className="text-left px-4 py-3">UTR</th>
                  <th className="text-left px-4 py-3">Screenshot</th>
                  <th className="text-left px-4 py-3">Submitted</th>
                  <th className="text-left px-4 py-3">Status</th>
                  <th className="text-right px-4 py-3">Action</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => {
                  const meta = STATUS_META[r.status] || STATUS_META.pending;
                  const Icon = meta.icon;
                  return (
                    <tr key={r.id} className="border-t border-border" data-testid={`payment-row-${r.id}`}>
                      <td className="px-4 py-3 font-medium text-charcoal">{r.business_name}</td>
                      <td className="px-4 py-3 text-right">{inr(r.amount)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-charcoal">{r.utr}</td>
                      <td className="px-4 py-3">
                        {r.screenshot_url ? (
                          <a href={resolveImg(r.screenshot_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-terracotta hover:underline" data-testid={`screenshot-link-${r.id}`}>
                            View <ExternalLink className="w-3 h-3" />
                          </a>
                        ) : <span className="text-charcoal-muted text-xs">—</span>}
                      </td>
                      <td className="px-4 py-3 text-xs text-charcoal-muted">
                        {new Date(r.submitted_at).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs uppercase tracking-widest font-semibold px-2 py-0.5 ${meta.cls}`} data-testid={`payment-status-${r.id}`}>
                          <Icon className="w-3 h-3" /> {meta.label}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        {r.status === "pending" ? (
                          <div className="inline-flex gap-2">
                            <button onClick={() => act(r.id, "verify")} disabled={!!busyId} data-testid={`verify-btn-${r.id}`}
                              className="text-xs bg-sage text-off-white px-3 py-1.5 hover:opacity-90 disabled:opacity-50 transition-opacity">
                              {busyId === r.id + "verify" ? "…" : "Verify"}
                            </button>
                            <button onClick={() => act(r.id, "reject")} disabled={!!busyId} data-testid={`reject-btn-${r.id}`}
                              className="text-xs border border-destructive text-destructive px-3 py-1.5 hover:bg-destructive/10 disabled:opacity-50 transition-colors">
                              {busyId === r.id + "reject" ? "…" : "Reject"}
                            </button>
                          </div>
                        ) : (
                          <span className="text-xs text-charcoal-muted">
                            {r.reviewed_at ? new Date(r.reviewed_at).toLocaleDateString("en-IN") : "—"}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
